import { getProjectEndDate } from './duration';
import { message } from "antd";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";

interface DataItem {
    key: string;
    summary: string;
    total: number | string | null;
    [key: string]: any;
    children?: DataItem[];
}


type ValidationError = {
    key: string;
    value: any;
    error: string;
};

/**
 * Function to validate an array of variables.
 * @param inputs - An array of variables (string, number, object, or array of objects).
 * @returns An array of validation errors, or an empty array if no errors are found.
 */
export const validateInputs = (inputs: any): ValidationError[] => {
    const errors: ValidationError[] = [];

    Object.values(inputs).forEach((value, index) => {
        const key = Object.keys(inputs)[index]
        if (value === null) {
            errors.push({ key, value, error: "Is a Required Field" });
        } else if (typeof value === "string" && value.trim() === "") {
            errors.push({ key, value, error: "Is a Required Field" });
        } else if (typeof value === "number" && isNaN(value)) {
            errors.push({ key, value, error: "Is a Required Field" });
        } else if (Array.isArray(value)) {
            value.forEach((item, i) => {
                Object.values(item).forEach((itemValue, itemIndex) => {
                    const objKey = Object.keys(item)[itemIndex]
                    if (itemValue === null || (typeof itemValue === "number" && isNaN(itemValue))) {
                        errors.push({
                            key: key + "-" + objKey,
                            value: itemValue,
                            error: `Value is Invalid`,
                        });
                    }
                });
            });
        }
    });

    return errors;
}


// function to calculate the number of days between two dates
export const calculateDaysBetweenDates = (date1: string, date2: string): number => {
    // Parse the input strings into Date objects
    const startDate = new Date(date1);
    const endDate = new Date(date2);

    // Normalize to midnight (ignore time)
    const startdateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const enddateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    // Calculate the difference in milliseconds
    const differenceInMilliseconds = enddateOnly.getTime() - startdateOnly.getTime();

    // Convert the difference to days
    const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

    return differenceInDays; // Return the signed difference
}

// function to add or subtract days to a date
export const addOrSubtractDays = (dateString: string, days: number): string => {
    // Parse the input date string into a Date object
    const date = new Date(dateString);

    // Add the given number of days (positive or negative)
    date.setDate(date.getDate() + days);

    // Return the updated date in Local format
    return dayjs(date).format("YYYY-MM-DDTHH:mm:ss");
}


export const addOrSubtractDaysForEndDate = (
  prevResourceEndDate: string,
  days: number,
  newProjectStartDate: string,
  prevProjectStartDate: string,
  prevResourceStartDate: string
): string => {
  if (
    areDatesEqualV2(prevResourceStartDate, prevProjectStartDate) &&
    isMovedByExactMonths(prevProjectStartDate, newProjectStartDate)
  ) {
    if (isLastDayOfMonth(prevResourceEndDate)) {
      return getShiftedEndDateByStartChange(
        prevProjectStartDate,
        newProjectStartDate,
        prevResourceEndDate
      );
    }
  }
  return addOrSubtractDays(prevResourceEndDate, days)
};

// function to check if an item's start date is before the project start date
export const isItemStartDateBeforeProjectStartDate = (itemStartDate: string, projectStartDate: string): boolean => {
    // Parse the date strings into Date objects
    // Parse MM/DD/YYYY format explicitly
    const [month1, day1, year1] = itemStartDate.split('/').map(Number);
    const [month2, day2, year2] = projectStartDate.split('/').map(Number);

    const itemDate = new Date(year1, month1 - 1, day1);
    const projectDate = new Date(year2, month2 - 1, day2);

    // Compare the dates
    return itemDate.getTime() < projectDate.getTime();
}

export const areDatesEqual = (date1: string, date2: string): boolean => {
    // Parse MM/DD/YYYY format explicitly
    const [month1, day1, year1] = date1.split('/').map(Number);
    const [month2, day2, year2] = date2.split('/').map(Number);

    const parsedDate1 = new Date(year1, month1 - 1, day1); // Month is 0-based in JS
    const parsedDate2 = new Date(year2, month2 - 1, day2);
    // Compare the time values
    return parsedDate1.getTime() === parsedDate2.getTime();
}

export const checkStartDateAndDuration = (startDate: string, duration: number): string => {
    let errorString = ''
    if (!startDate && duration)
        errorString = "Start Date is Invalid"
    else if (startDate && !duration)
        errorString = "Duration is Invalid"
    else if (!startDate && !duration)
        errorString = "Start Date and Duration are Invalid"

    return errorString
}

export const calculateStartDate = (
    projectStartDate: string,
    durationInMonths: number,
    startDate: string,
): string => {
    const start = new Date(startDate);
    const projectStart = new Date(projectStartDate);
    if (!Number.isInteger(durationInMonths)) {
        const projectEnd = getProjectEndDate(projectStartDate, durationInMonths);
        return (start < projectStart ? dayjs(projectStart) : dayjs(start).isAfter(projectEnd) ? projectEnd : dayjs(start)).format('YYYY-MM-DDTHH:mm:ss');
    }
    // Add the specified number of months to the startDate
    const adjustedEndDate = new Date(
        projectStart.getFullYear(),
        projectStart.getMonth() + durationInMonths,
        projectStart.getDate(),
        projectStart.getHours(),
        projectStart.getMinutes(),
        projectStart.getSeconds(),
        projectStart.getMilliseconds()
    );
    // Get the last day of the month of adjustedEndDate
    const lastDayOfMonth = new Date(
        adjustedEndDate.getFullYear(),
        adjustedEndDate.getMonth() + 1, // Next month
        0 // Day 0 gives the last day of the previous month
    );
    return start < projectStart ? dayjs(projectStart).format("YYYY-MM-DDTHH:mm:ss") : start < lastDayOfMonth ? dayjs(start).format("YYYY-MM-DDTHH:mm:ss") : dayjs(adjustedEndDate).format("YYYY-MM-DDTHH:mm:ss");
};

export const calculateEndDate = (
    startDate: string,
    durationInMonths: number,
    endDate: string
): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!Number.isInteger(durationInMonths)) {
        const projectEnd = getProjectEndDate(startDate, durationInMonths);
        return (dayjs(end).isAfter(projectEnd) ? projectEnd : dayjs(end)).format('YYYY-MM-DDTHH:mm:ss');
    }
    // Add the specified number of months to the startDate
    const adjustedEndDate = new Date(
        start.getFullYear(),
        start.getMonth() + durationInMonths,
        start.getDate(),
        start.getHours(),
        start.getMinutes(),
        start.getSeconds(),
        start.getMilliseconds()
    );
    // Check if the month has rolled over
    if (adjustedEndDate.getMonth() !== (start.getMonth() + durationInMonths) % 12) {
        // Set the date to the last day of the previous month
        adjustedEndDate.setDate(0);
    }

    // Get the last day of the month of adjustedEndDate
    const lastDayOfMonth = new Date(
        adjustedEndDate.getFullYear(),
        adjustedEndDate.getMonth() + 1, // Next month
        0 // Day 0 gives the last day of the previous month
    );
    return end <= lastDayOfMonth 
        ? dayjs(end).format("YYYY-MM-DDTHH:mm:ss") 
        : dayjs(adjustedEndDate).format("YYYY-MM-DDTHH:mm:ss");
};

export const isLastDayOfMonth = (dateStr: string): boolean => {
    const date = new Date(dateStr.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));

    // Get the last day of the current month
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);


    return date.getDate() === lastDay.getDate();
};


export const isMovedByExactMonths = (dateStr1: string, dateStr2: string): boolean => {
    const date1 = new Date(dateStr1.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));
    const date2 = new Date(dateStr2.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));

    // Ensure date1 is always the earlier date
    const earlier = date1 < date2 ? date1 : date2;
    const later = date1 < date2 ? date2 : date1;

    const yearDiff = later.getFullYear() - earlier.getFullYear();
    const monthDiff = later.getMonth() - earlier.getMonth();
    const totalMonthDiff = yearDiff * 12 + monthDiff;

    // Check if the days match exactly and there's at least 1 full month difference
    return totalMonthDiff >= 1 && (earlier.getDate() === later.getDate() || (isLastDayOfMonth(dateStr1.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, "")) && isLastDayOfMonth(dateStr2.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""))))
};

export const getShiftedEndDateByStartChange = (
    prevStartDate: string,
    newStartDate: string,
    prevEndDate: string
): string => {
    const oldStart = new Date(prevStartDate.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));
    const newStart = new Date(newStartDate.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));
    const oldEnd = new Date(prevEndDate.replace(/Z$/, '').replace(/[-+]\d{2}:\d{2}$/, ""));

    const yearDiff = newStart.getFullYear() - oldStart.getFullYear();
    const monthDiff = newStart.getMonth() - oldStart.getMonth();
    const totalMonthShift = yearDiff * 12 + monthDiff;

    // Create new end date by shifting old end date by the month difference
    const newEndDate = new Date(
        oldEnd.getFullYear(),
        oldEnd.getMonth() + totalMonthShift,
        oldEnd.getDate()
    );


    // Check if the month has rolled over
    if (newEndDate.getMonth() !== (oldEnd.getMonth() + totalMonthShift) % 12) {
        // Set the date to the last day of the previous month
        newEndDate.setDate(0);
    }

    // Get the last day of the month of adjustedEndDate
    const lastDayOfMonth = new Date(
        newEndDate.getFullYear(),
        newEndDate.getMonth() + 1, // Next month
        0 // Day 0 gives the last day of the previous month
    );

    return dayjs(lastDayOfMonth).format("YYYY-MM-DDTHH:mm:ss");
};

export const areDatesEqualV2 = (dateStr1: string, dateStr2: string): boolean => {
    return dateStr1.split('T')[0] == dateStr2.split('T')[0]
};


/**
 * Updates the discount percentage and amount based on a desired total amount.
 * This function calculates what discount percentage and amount would be needed
 * to achieve a specific total amount after contingency.
 * 
 * @param value - The desired total amount after discount
 * @param setDiscountValue - State setter for the discount percentage
 * @param setDiscountAmount - State setter for the discount amount
 * @param dealEconomicsSummaries - Array containing project pricing information
 * @param contingencyValue - The contingency percentage to be applied
 */
export const changeTotalAmount = (
    value: number | null,
    setDiscountValue: Dispatch<SetStateAction<number>>,
    setDiscountAmount: Dispatch<SetStateAction<number>>,
    dealEconomicsSummaries: any,
    contingencyValue: number
) => {
    if (
        dealEconomicsSummaries.length > 0 &&
        "Total Project Price" in dealEconomicsSummaries[0]
    ) {
        // Get the base project price before any adjustments
        const baseAmount = Number(dealEconomicsSummaries[0]["Total Project Price"]);
        // Calculate total with contingency added
        const totalWithContingency = baseAmount + (baseAmount * (contingencyValue / 100));
        
        // Calculate the required discount to reach the desired total amount
        const requiredDiscount = totalWithContingency - (value || 0);
        // Convert the discount amount to a percentage
        const discountPercentage = (Math.round(requiredDiscount) / Math.round(totalWithContingency)) * 100;
        

        // Validate and adjust if discount percentage is outside acceptable range
        if (discountPercentage > 100 || discountPercentage < -100) {
            message.error("Discount(%) Must be Below 100");
            if (discountPercentage > 100) {
                // Cap at 100% discount
                setDiscountValue(100);
                setDiscountAmount(Math.round(totalWithContingency));
            } else {
                // Cap at -100% discount (effectively doubling the total revenue)
                setDiscountValue(-100);
                setDiscountAmount(Math.round(totalWithContingency * -1));
            }
        }
        else {
            // Update both the discount percentage and amount
            setDiscountValue(Number(discountPercentage));
            setDiscountAmount(Math.round(requiredDiscount));
        }

    }
};

/**
 * Updates the discount amount based on a percentage value.
 * This function calculates the actual discount amount when a percentage is provided.
 * 
 * @param value - The discount percentage (can be positive or negative)
 * @param setDiscountValue - State setter for the discount percentage
 * @param setDiscountAmount - State setter for the discount amount
 * @param dealEconomicsSummaries - Array containing project pricing information
 * @param contingencyValue - The contingency percentage to be applied
 */
export const changeDiscountValue = (
    value: number | null,
    setDiscountValue: Dispatch<SetStateAction<number>>,
    setDiscountAmount: Dispatch<SetStateAction<number>>,
    dealEconomicsSummaries: any,
    contingencyValue: number
) => {
    // Update the discount percentage state
    setDiscountValue(value ? value : 0);
    if (
        dealEconomicsSummaries.length > 0 &&
        "Total Project Price" in dealEconomicsSummaries[0]
    ) {
        // Get the base project price
        const baseAmount = Number(dealEconomicsSummaries[0]["Total Project Price"]);
        // Calculate total with contingency
        const totalWithContingency = baseAmount + (baseAmount * (contingencyValue / 100));
        // Calculate the actual discount amount based on the percentage
        const discountAmount = Math.round(totalWithContingency * ((value ? value : 0) / 100));
        setDiscountAmount(discountAmount);
    }
};

/**
 * Updates the discount percentage based on a specific discount amount.
 * This function calculates the corresponding percentage when a specific discount amount is provided.
 * Includes validation to ensure the discount percentage stays within -100% to 100%.
 * 
 * @param value - The desired discount amount
 * @param setDiscountValue - State setter for the discount percentage
 * @param setDiscountAmount - State setter for the discount amount
 * @param dealEconomicsSummaries - Array containing project pricing information
 * @param contingencyValue - The contingency percentage to be applied
 */
export const changeDiscountAmount = (
    value: number | null,
    setDiscountValue: Dispatch<SetStateAction<number>>,
    setDiscountAmount: Dispatch<SetStateAction<number>>,
    dealEconomicsSummaries: any,
    contingencyValue: number
) => {
    // Update the discount amount state
    setDiscountAmount(value ? value : 0);
    if (
        dealEconomicsSummaries.length > 0 &&
        "Total Project Price" in dealEconomicsSummaries[0]
    ) {
        // Get the base project price
        const baseAmount = Number(dealEconomicsSummaries[0]["Total Project Price"]);
        // Calculate total with contingency
        const totalWithContingency = baseAmount + (baseAmount * (contingencyValue / 100));
        // Calculate the discount percentage based on the amount
        let disc_value = ((value ? value : 0) / totalWithContingency) * 100;
        setDiscountValue(disc_value);
        
        // Validate and adjust if discount percentage is outside acceptable range
        if (disc_value > 100 || disc_value < -100) {
            message.error("Discount(%) Must be Below 100");
            if (disc_value > 100) {
                // Cap at 100% discount
                setDiscountValue(100);
                setDiscountAmount(Math.round(totalWithContingency));
            } else {
                // Cap at -100% discount (effectively doubling the total revenue)
                setDiscountValue(-100);
                setDiscountAmount(Math.round(totalWithContingency * -1));
            }
        }
    }
};

export const convertDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-");  // Split "YYYY-MM-DD"
    return `${month}/${day}/${year}`;  // Format as "MM/DD/YYYY"
};

export const roundOffMonthlyValues = (data: DataItem[]): DataItem[] => {
    const isNumericValue = (val: any): val is number => typeof val === 'number' && !isNaN(val);

    const adjustRow = (row: DataItem): DataItem => {
        if (row.summary.includes('Margin %')) {
            return { ...row }; // Return untouched
        }

        const monthKeys = Object.keys(row).filter(k => k.startsWith('month') && isNumericValue(row[k]));

        const monthSum = monthKeys.reduce((sum, k) => sum + (isNumericValue(row[k]) ? row[k] : 0), 0);

        const newRow = { ...row };

        if (isNumericValue(newRow.total) && monthSum !== 0) {
            const diff = newRow.total - monthSum;
            const largestKey = monthKeys.reduce((maxKey, key) =>
                (newRow[key] > newRow[maxKey] ? key : maxKey), monthKeys[0]);

            if (isNumericValue(newRow[largestKey])) {
                newRow[largestKey] += diff;
            }
        }

        // Recursively adjust children
        if (Array.isArray(newRow.children)) {
            newRow.children = newRow.children.map(child => {
                const childMonthKeys = Object.keys(child).filter(k => k.startsWith('month') && isNumericValue(child[k]));
                const childMonthSum = childMonthKeys.reduce((sum, k) => sum + (isNumericValue(child[k]) ? child[k] : 0), 0);

                const newChild = { ...child };

                if (isNumericValue(newChild.total) && childMonthSum !== 0) {
                    const diff = newChild.total - childMonthSum;
                    const largestChildKey = childMonthKeys.reduce((maxKey, key) =>
                        (newChild[key] > newChild[maxKey] ? key : maxKey), childMonthKeys[0]);

                    if (isNumericValue(newChild[largestChildKey])) {
                        newChild[largestChildKey] += diff;
                    }
                }

                return newChild;
            });
        }

        return newRow;
    };

    // Return new adjusted data array
    return data.map(adjustRow);
}

export const formatCurrency = (amount: number): string => {
  try {
    return `$ ${Math.round(amount)?.toLocaleString("en-US", {
      minimumFractionDigits: 0,
    })}`;
  } catch (error) {
    console.error(error);
    return "$ 0";
  }
};

export const generateTableColumnFilters = (data: any[], key: string): any[] => {
    const uniqueValues = Array.from(
        new Set(data.map((item) => {
            try {
                return item[key].toString();
            } catch (error) {
                console.error('Error converting item to string:', { item, key, data });
                return '';
            }
        }))
    ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return uniqueValues.map((value) => ({ text: value, value }));
}
